import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';

import {FormBuilder, FormGroup, FormsModule, Validators, ReactiveFormsModule} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReceivableSearchService } from '../receivable-search.service';
import { ReceivableDomain } from '../domain/receivable-domain';

import { MatRadioModule, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaymentService } from '../payment.service';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, ObservableInput } from 'rxjs';


@Component({
  selector: 'app-payment-search-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,
    MatRadioModule, MatRadioGroup, MatRadioButton, MatInputModule, MatFormFieldModule, MatButtonModule,
   MatDialogActions, MatCardModule, MatProgressSpinnerModule ],
  templateUrl: './payment-search-modal.component.html',
  styleUrl: './payment-search-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: []
})
export class PaymentSearchModalComponent {
  readonly dialogRef = inject(MatDialogRef<PaymentSearchModalComponent>);
  currentRevenueType: string = '';
  invalidReceivableType: string = '';
  form!: FormGroup;
  searchInProgress: boolean = false;
  // errorMessages: string[] = [];
  getErrorTransactionIDMismatch() {
    return 'Transaction ID and re-enter transaction ID do not match.';
  }
  getErrorTransactionIDNull() {
    return 'A transaction ID is required.';
  }
  getErrorCheckTransactionIDNull() {
    return 'A re-enter transaction ID is required.';
  }
  getErrorReceivableIDMismatch() {
    return 'Receivable ID and re-enter receivable ID do not match.';
  }
  getErrorReceivableIDNull() {
    return 'A receivable ID is required.';
  }
  getErrorCheckReceivableIDNull() {
    return 'A re-enter receivable ID is required.';
  }
  getErrorIncorrectRevenueType(type: string) {
    return 'The revenue entered is not the same type as the existing set of revenues ('+ type + '). You can only pay multiple revenues of the same type.';
  }
  getErrorRevenueAlreadyPaid() {
    return 'This revenue has already been paid.';
  }
  getErrorInvalidReceivableType(type: string) {
    return 'The revenue code ' + type + ' is not currently active. Please pay the invoice by US postal mail with a check or money order.';
  }
  getErrorInactiveReceivable() {
    return 'This revenue can\'t be paid online at this time. Please pay by postal mail or contact our Fiscal Office at (614) 644-2339';
  }
  getErrorDuplicate(id: string) {
    return 'Receivable ID ' + id + ' has already been added.';
  }
  getErrorTransactionRevenueLocked(id: string) {
    return 'The revenue ID ' + id + ' associated with this transaction has a pending online payment attempt and is currently locked. This invoice cannot be paid online at this time.  If you feel this is in error please contact our Fiscal Office at (614) 644-2339.';
  }
  getErrorTransactionPaid() {
    return 'This transaction has already been paid.';
  }
  getErrorTransactionZeroBalance() {
    return 'This transaction has $0.00 amount due and does not require payment at this time.';
  }

  searchType: string = '';
  searchTypes: string[] = ['Receivable ID', 'Transaction ID'];
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  private _snackBar = inject(MatSnackBar);

  openSnackBar(message: string, action: string, severity: string) {
    if (severity=='info') {
      this._snackBar.open(message, action, {duration: 3200, panelClass: ['mat-toolbar', 'green-snackbar']});
    } else if (severity=='error') {
      this._snackBar.open(message, action, {panelClass: ['mat-toolbar', 'red-snackbar']});
    }
    this.searchInProgress = false;
  }

  constructor(
    private fb: FormBuilder, 
    private receivableSearchService: ReceivableSearchService,
    private paymentService: PaymentService
  ) {
    if (this.receivableSearchService.getReceivableList().length > 0) {
      this.currentRevenueType = this.receivableSearchService.getReceivableList()[0].typeCode;
    };

    this.form = this.fb.group({
      searchType: ['', Validators.required],
      receivableID: [null],
      checkReceivableID: [null],
      transactionID: [null],
      checkTransactionID: [null],
    });
    this.form.get('searchType')?.valueChanges.subscribe(searchType => {
      this.searchType = searchType;
      this.form.get('receivableID')?.setValue(null);
      this.form.get('receivableID')?.updateValueAndValidity();
      this.form.get('checkReceivableID')?.setValue(null);
      this.form.get('checkReceivableID')?.updateValueAndValidity();

      this.form.get('transactionID')?.setValue(null);
      this.form.get('transactionID')?.updateValueAndValidity();
      this.form.get('checkTransactionID')?.setValue(null);
      this.form.get('checkTransactionID')?.updateValueAndValidity();
      
      // this.errorMessages = [];
    })
  }

  continueClick() {
    // this.errorMessages = [];
    this.searchInProgress = true;
    if (this.searchType === 'Receivable ID') {
      if (this.form.get('receivableID')?.value == null || this.form.get('checkReceivableID')?.value == null) {
        if (this.form.get('receivableID')?.value == null) {
          // this.errorMessages.push(this.getErrorReceivableIDNull());
          this.openSnackBar(this.getErrorReceivableIDNull(), '', 'error');
        }
        if (this.form.get('checkReceivableID')?.value == null) {
          // this.errorMessages.push(this.getErrorCheckReceivableIDNull());
          this.openSnackBar(this.getErrorCheckReceivableIDNull(), '', 'error');
        }
      } else {
        if (this.form.get('receivableID')?.value == this.form.get('checkReceivableID')?.value) {
          const receivableID = this.form.get('receivableID')?.value || -1;
          this.searchByReceivableID(receivableID);
        } else {
          // this.errorMessages.push(this.getErrorReceivableIDMismatch());
          this.openSnackBar(this.getErrorReceivableIDMismatch(), '', 'error');
        }
      }
    } else if (this.searchType === 'Transaction ID' ) {
      if (this.form.get('transactionID')?.value == null || this.form.get('checkTransactionID')?.value == null) {
        if (this.form.get('transactionID')?.value == null) {
          // this.errorMessages.push(this.getErrorTransactionIDNull());
          this.openSnackBar(this.getErrorTransactionIDNull(), '', 'error');
        }
        if (this.form.get('checkTransactionID')?.value == null) {
          // this.errorMessages.push(this.getErrorCheckTransactionIDNull());
          this.openSnackBar(this.getErrorCheckTransactionIDNull(), '', 'error');
        }
      } else {
        if (this.form.get('transactionID')?.value == this.form.get('checkTransactionID')?.value) {
          const transactionID = this.form.get('transactionID')?.value || -1;
          this.searchByTransactionID(transactionID);
        } else {
          // this.errorMessages.push(this.getErrorTransactionIDMismatch());
          this.openSnackBar(this.getErrorTransactionIDMismatch(), '', 'error');
        }
      }
    }
  }

  public resetClick() {
    this._snackBar.dismiss();
    this.form.reset();
    this.invalidReceivableType = '';
  }

  private searchByReceivableID(receivableID: number) {
    this.receivableSearchService.retrieveReceivableByID(receivableID).subscribe({
      next: result => {
      if (!result) {
        this.openSnackBar('No receivable found for Receivable ID: ' + receivableID, '', 'info');
      } else {
        this.handleReturnedReceivable(result.data);
      }
    }, error: err => {        
      this.openSnackBar('Failed retrieving receivable: ' + err.message, '', 'error');
    }});
  }

  handleReturnedReceivable(receivable: ReceivableDomain) {
    if (this.currentRevenueType != '' && this.currentRevenueType != receivable.typeCode) {
      this.openSnackBar(this.getErrorIncorrectRevenueType(this.currentRevenueType), '', 'error');
      // this.errorMessages.push(this.getErrorIncorrectRevenueType(this.currentRevenueType));
    } else if (receivable.isPaid) {
      this.openSnackBar(this.getErrorRevenueAlreadyPaid(), '', 'error');
      // this.errorMessages.push(this.getErrorRevenueAlreadyPaid());
    } else if (receivable.paymentECACId == null) {
      this.openSnackBar(this.getErrorInvalidReceivableType(receivable.typeCode), '', 'error');
      // this.errorMessages.push(this.getErrorInvalidReceivableType(receivable.typeCode));
    } else if (receivable.isCertified || receivable.isInactive) {
      this.openSnackBar(this.getErrorInactiveReceivable(), '', 'error');
      // this.errorMessages.push(this.getErrorInactiveReceivable());
    } else if (this.isDuplicate(receivable)) {
      this.openSnackBar(this.getErrorDuplicate(receivable.id.toString()), '', 'error');
      // this.errorMessages.push(this.getErrorDuplicate(receivable.id.toString()));
    } else {
      this.paymentService.checkPaymentUnlocked(receivable.id.toString()).subscribe({
        next: () => {
          this.receivableSearchService.addReceivableToList(receivable);
          this.closeDialog(receivable);
        }, error: (err: HttpErrorResponse) => {
          // this.errorMessages.push(err.error?.message || err.message);
          this.openSnackBar(err.error?.message || err.message, '', 'error');
        }
      });
    }
  }

  private searchByTransactionID(transactionID: number) {
    // transactionID = 1;
    // if (this.receivableSearchService.getReceivableList().length>0) {
    //   transactionID = 2;
    // }

    this.receivableSearchService.retrieveReceivableByTransactionID(transactionID).subscribe({
      next: result => {
      if (!result) {
        this.openSnackBar('No receivable found for Transaction ID: ' + transactionID, '', 'info');
      } else if (result.data == null || result.data.length == 0) {
        this.openSnackBar('No invoice information was found for Transaction ID: ' + transactionID, '', 'info');
      } else {
        this.handleReturnedReceivableList(result.data);
      }
    }, error: err => {      
      this.openSnackBar('Failed retrieving receivable: ' + err.message, '', 'error');
    }});
  }

  handleReturnedReceivableList(receivables: ReceivableDomain[]) {
    let isTransactionPaid: boolean = true;
    let isARevenueLocked: boolean = false;
    let totalAmountDue: number = 0.00;
    const restCalls: ObservableInput<any>[] = [];

    receivables.forEach(receivable => {
      if (!receivable.isPaid) {
        isTransactionPaid = false;
      }

      restCalls.push(this.paymentService.checkPaymentUnlocked(receivable.id.toString()));
      totalAmountDue += receivable.balanceDue;
      
    });

    forkJoin(restCalls)
    // .pipe(catchError(error : error => this.openSnackBar(err.error?.message || err.message, '', 'error'); ))
    .subscribe({next: (results:any) => {
      for (let index = 0; index < results.length; index++) {
        let booleanResult : boolean = results[index];
        if (booleanResult == false) {
          this.openSnackBar(this.getErrorTransactionRevenueLocked(receivables[index].id.toString()), '', 'error');
          isARevenueLocked = true;
        }
      }

      if (isTransactionPaid) {
        this.openSnackBar(this.getErrorTransactionPaid(), '', 'error');
      } else if (totalAmountDue == 0) {  // Look for a zero amount due
        this.openSnackBar(this.getErrorTransactionZeroBalance(), '', 'error');
      } else if (!isARevenueLocked) {
        receivables.forEach(receivable => {
          if (!receivable.isPaid) {
            this.receivableSearchService.addReceivableToList(receivable);
            this.closeDialog(receivable);
          }
        });
      }
    }, error: (err: HttpErrorResponse) => {
      this.openSnackBar(err.error?.message || err.message, '', 'error');
    }});
  }

  isDuplicate(receivable: ReceivableDomain) {
    const index: number = this.receivableSearchService.getReceivableList().findIndex(item => (item.id == receivable.id));
    if(index>= 0){ // found
      return true;
    }
    return false;
  }

  closeDialog(receivable?: ReceivableDomain|undefined) {
    this._snackBar.dismiss();
    this.dialogRef.close(receivable);
    this.searchInProgress = false;
  }

  public enableSearch() {
    if (this.form.get('searchType')?.value) {
      return true;
    } else {
      return false;
    }
  //   if (this.form.get('searchType')?.value == 'Receivable ID' && this.form.get('receivableID')?.value == this.form.get('checkReceivableID')?.value) {
  //     return true;
  //   } else if (this.form.get('searchType')?.value == 'Transaction ID' && this.form.get('transactionID')?.value == this.form.get('checkTransactionID')?.value) {
  //     return true;
  //   } else {
  //     return false;
  //   }
  }

}
